<template>
  <div v-if="currentVideoRoom">
    <div v-if="isLoading" class="text-center spinner-border mt-3" role="status">
      <span class="sr-only">Loading...</span>
    </div>
    <div v-else-if="videoRoom">
      <h2 class="card-title mb-4 text-left" v-if="!magicToken">
        Video Room
        <span :class="`video-${currentVideoRoom.status} badge font-weight-medium float-right`">{{
          currentVideoRoom.status
        }}</span>
        <CallTimer :callTimestamp="videoRoom.created_at" v-if="currentVideoRoom.status !== 'completed'"/>
      </h2>
      <h2 class="card-title mb-4 text-left" v-else-if="magicToken">
        Video Room
        <span :class="`video-${currentVideoRoom.status} badge font-weight-medium float-right`">{{
          currentVideoRoom.status
        }}</span>
        <CallTimer :callTimestamp="videoRoom.created_at" v-if="currentVideoRoom.status !== 'completed'"/>
      </h2>
      <div v-if="!magicToken && currentVideoRoom.status == 'in-progress' && !error" class="mb-3">
        <div v-if="currentVideoRoom.participants && currentVideoRoom.participants.length > 0">
          <p
            v-for="participant in currentVideoRoom.participants"
            :key="participant.ParticipantSid"
            class="pb-1 ml-2"
          >
            {{ participant.ParticipantIdentity ? participant.ParticipantIdentity : participant.identity }} 
            has joined the video room. 
            <span
              v-if="(participant.ParticipantIdentity ? participant.ParticipantIdentity : participant.identity) !== currentAgency.agency_name"
              @click="() => removeParticipant(participant)"
              style="cursor: pointer; font-size: 16px; font-weight: bold;"
            >&times;</span>
          </p>
        </div>
        <div v-else>Waiting for participant to join...</div>
      </div>
      <p v-else-if="error">{{ error }}</p>
      <div style="height: min-content" v-if="embeddedVideo && !error">
        <VideoRoomWidget
          v-if="joined && videoAccessToken"
          :roomId="xRoomId"
          :token="videoAccessToken"
          :resourceId="videoResourceId"
          :participantRole="participantRole"
          :startLocalVideo="magicToken ? (isTrueOrFalse(magicToken.token_data.anonymous) ? false : true) : userVideoRequestPersisted.enableVideo"
          :startLocalAudio="magicToken ? (isTrueOrFalse(magicToken.token_data.anonymous) ? false : true) : userVideoRequestPersisted.enableAudio"
          :anonymous="magicToken ? isTrueOrFalse(magicToken.token_data.anonymous) : false"
          :startTranscription="magicToken ? isTrueOrFalse(magicToken.token_data.enableTranscription) : userVideoRequestPersisted.enableTranscription"
          :blurVideo="magicToken ? false : userVideoRequestPersisted.blurVideo"
          :agencyLogo="agencyLogo"
          :magicToken="magicToken"
        />
        <div v-else-if="agencyLogo">
          <img :src="agencyLogo" style="max-width:50vh" />
        </div>
      </div>
      <div
        v-if="currentVideoRoom.status == 'in-progress' && ((!magicToken && generalConditionForCTA) || magicToken)"
        class="row border-top pt-3"
      >
        <div class="video-room-actions d-flex justify-content-end align-items-center">
          <button
            v-if="!magicToken"
            type="button"
            class="btn btn-small btn-rounded btn-info new-feature"
            :disabled="pendingVideoOperation"
            @click="shareVideoRoom()"
          >
            Share<b-badge variant="danger" class="beta-badge">BETA</b-badge>
          </button>
          <button
            v-if="joined"
            type="button"
            class="btn btn-small btn-rounded btn-warning"
            :disabled="pendingVideoOperation"
            @click="leaveRoom()"
          >Leave</button>
          <button
            v-else
            type="button"
            class="btn btn-small btn-rounded btn-success"
            :disabled="pendingVideoOperation"
            @click="joinRoom()"
          >Join</button>
          <button
            v-if="!magicToken"
            @click="endRoom()"
            type="button"
            class="btn btn-small btn-rounded btn-danger"
            :disabled="pendingVideoOperation"
          >End</button>
        </div>
      </div>
    </div>
    <div v-else-if="roomId">Loading Video Room...</div>
    <div v-else>{{ error }}</div>
    <b-sidebar v-if="magicToken && joined" v-model="showLocationSidebar" width="100vw">
      <div v-if="magicToken && joined && sharingLocation" class="participant-location">
        <GeoFinder :magicToken="magicToken" @postedLocation="showLocationSidebar = false" />
      </div>
    </b-sidebar>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters, mapState } from "vuex";
import { EventBus } from "../events/eventBus";
import VideoRoomWidget from "./widgets/VideoRoomWidget";
import GeoFinder from "./GeoFinder";
import VideoRoomShare from "./VideoRoomShare";
import { recognitionLanguages } from "../constants";
import CallTimer from "./CallTimer";

export default {
  data() {
    return {
      error: null,
      isLoading: false,
      videoRoom: null,
      videoAccessToken: null,
      videoResourceId: null,
      joined: false,
      token: null,
      xRoomId: null,
      participant: null,
      embeddedVideo: false,
      sharingLocation: false,
      showLocationSidebar: false,
      pendingVideoOperation: false,
    };
  },
  components: {
    VideoRoomWidget,
    GeoFinder,
    CallTimer
  },
  props: {
    roomId: String,
    magicToken: Object
  },
  async created() {
    this.showLocationSidebar = false;
    this.sharingLocation = false;
    if (this.magicToken && !this.roomId) {
      this.xRoomId = this.magicToken.token_data.room_id;
      this.participant = this.anonymous ? this.magicToken.token_data.anonymousLabel : this.magicToken.token_data.recipient;
      this.agencyLogo = this.magicToken.token_data.agency_logo;
    } else {
      this.xRoomId = this.roomId;
      this.participant = this.currentAgency.agency_name;
      this.agencyLogo = this.$cookie.get("x-agency-logo");
    }

    await this.fetchVideoRoom();
    EventBus.$emit("video.join-room", this.xRoomId);

    if (this.magicToken && confirm("Join video session?")) {
      this.embeddedVideo = true;
      await this.joinRoom();
    }

    if (!this.magicToken) {
      const conversationId = this.$route.params.conversationId;
      if (this.videoRoomCreated[`${conversationId}`]) {
        await this.joinRoom();
      }
    }

    EventBus.$on("video.bus", this.handleVideoSignal);
    EventBus.$on("video.share-location", async (data) => {
      if (this.magicToken.token_data.recipient == data.identity && confirm("Will you share your location?")) {
        this.showLocationSidebar = true;
        this.sharingLocation = true;
      }
    });
  },
  beforeDestroy() {
    this.joined = false;
    EventBus.$off("video.share-location");
  },
  computed: {
    ...mapGetters("user", ["currentToken", "currentUser"]),
    ...mapGetters("persist", ["userVideoRequestPersisted"]),
    ...mapGetters("agency", ["currentAgency", "currentAgencyId", "agencySettings"]),
    ...mapGetters("video", ["videoRooms", "videoRoomCreated"]),
    ...mapGetters("conversations", ["currentConversation"]),
    ...mapState({
        agencyVideoRoomJoined: (state) => state.agency.agencyVideoRoomJoined,
    }),
    generalConditionForCTA() {
      const { status, operator_id } = this.currentConversation;
      const { enableConversationAssignment } = this.agencySettings;

      if (status === "active") {
        if (enableConversationAssignment === false) {
          return true;
        }
        if (enableConversationAssignment === true && (operator_id === this.currentUser.user_id || (operator_id !== null && this.$auth.can('admin:agency')))) {
          return true;
        }
      }
      return false;
    },
    currentVideoRoom() {
      return this.videoRooms[this.videoRooms.findIndex(room => room.room_id === this.xRoomId)];
    },
    participants() {
      return this.videoRooms[this.videoRooms.findIndex(room => room.room_id === this.xRoomId)].participants;
    },
    participantRole() {
      return this.magicToken ? "citizen" : "dispatch";
    },
    autoplayVideo() {
      return this.participantRole == "citizen" ? true : false;
    },
    videoControls() {
      return this.participantRole == "dispatch" ? true : false;
    },
  },
  methods: {
    handleVideoSignal(data) {
      if (data.event == "error") {
        this.error = data.message;
        this.joined = false;
      }
      else if (data.event == "disconnect")
        this.joined = false;
    },
    leaveRoom() {
      EventBus.$emit("video.leave-room", { roomId: this.xRoomId });
      EventBus.$emit("closeVideoModal");
      this.joined = false;
    },
    shareVideoRoom() {
      const $ = window.$;
      $("#id-modal-container").modal({
        backdrop: "static"
      });
      EventBus.$emit("open", {
        component: VideoRoomShare,
        title: "Share video room",
        closeOnClick: false,
        props: {
          roomId: this.roomId,
        }
      });
    },
    async removeParticipant(participant) {
      try {
        const participantId = participant.ParticipantSid ? participant.ParticipantSid : participant.sid;
        await this.$ConveyApi.videoApi.deleteVideoRoomParticipant({
          roomId: this.roomId,
          participantId
        });
      } catch (err) {
        console.log(err);
      }
    },
    async fetchVideoRoom() {
      try {
        const resp = await this.$ConveyApi.videoApi.getVideoRoom({ roomId: this.xRoomId });
        this.videoRoom = resp.data.data;
        this.$store.commit("video/upsertVideoRoom", resp.data.data);
      } catch (error) {
        this.error = "Got an error trying to retrieve video room. Please try again.";
        this.isLoading = false;
      }
    },
    async endRoom() {
      this.pendingVideoOperation = true;
      if (!this.$auth.isAuthenticated) {
        this.pendingVideoOperation = false;
        return;
      }

      if (!confirm("Are you sure you want to close and end this video session?")) {
        this.pendingVideoOperation = false;
        return;
      }

      try {
        const resp = await this.$ConveyApi.videoApi.deleteVideoRoom({ roomId: this.xRoomId });
        this.$store.commit("video/upsertVideoRoom", resp.data.data);
        this.leaveRoom();
        this.$toastr("success", "Video session has been closed.");
        this.pendingVideoOperation = false;
      } catch (error) {
        this.$toastr("error", "Error closing video session. Please try again");
        this.pendingVideoOperation = false;
      }
    },
    async joinRoom() {
      this.error = null;
      this.pendingVideoOperation = true;
      if (this.videoAccessToken)
        this.joined = true;

      if (this.joined && this.videoAccessToken && this.videoResourceId) {
        if (this.magicToken)
          this.embeddedVideo = true;
        else
          this.videoRoomModal();

        this.pendingVideoOperation = false;
        return;
      }

      if (this.magicToken) {
        if (!this.videoAccessToken && !this.videoResourceId) {
          this.embeddedVideo = true;
        }
      }

      if (!this.magicToken) {
        await this.$ConveyApi.conversationsApi.getConversation({ conversationId: this.$route.params.conversationId })
          .then(resp => {
            if (resp.data.data.recipients) {
              resp.data.data.recipients.forEach(recipient => {
                const { number, language } = recipient;
                const recogLang = recognitionLanguages.find(item => item.split('-')[0] == language)

                if (!recogLang) return;

                const option = {};
                option[number] = recogLang;
                this.$store.commit("transcription/setRecognitionLanguages", option);
              })
            }
          }).catch(err => {
            const errorResponse = err.response;
            if (errorResponse.data.data.error) {
              this.$toastr("error", errorResponse.data.data.error)
            } else {
              this.$toastr("error", err);
            }
          });
      }

      await this.$ConveyApi.videoApi.createVideoRoomAccessToken({
        roomId: this.xRoomId,
        payload: { participant: this.participant },
      }).then(resp => {
        this.videoAccessToken = resp.data.data.access_token;
        this.videoResourceId = resp.data.data.resource_id;
        this.joined = true;

        if (!this.magicToken) {
          const conversationId = this.$route.params.conversationId;
          if (this.videoRoomCreated[`${conversationId}`]) {
            this.videoRoomModal();
            this.$store.commit("video/setVideoRoomCreated", { status: false, conversationId });
          }
        }
      }).catch(err => {
        const errorResponse = err.response;
        if (errorResponse.status === 400) {
          this.$toastr("error", "Video session ended: It seems you're trying to join a concluded Video meeting. Please verify the details or reach out to the organizer.")
        } else if (errorResponse.data.data.error) {
          this.$toastr("error", `Error joining video room. ${errorResponse.data.data.error}`)
        } else {
          this.$toastr("error", `Error joining video room. Please try again. (${err})`);
        }
      }).finally(() => {
        this.pendingVideoOperation = false;
      });
    },
    videoRoomModal() {
      const videoRoomModalProps = {
        roomId: this.xRoomId,
        token: this.videoAccessToken,
        resourceId: this.videoResourceId,
        participantRole: this.participantRole,
        agencyLogo: this.agencyLogo,
        startLocalVideo: this.userVideoRequestPersisted.enableVideo,
        startLocalAudio: this.userVideoRequestPersisted.enableAudio,
        startTranscription: this.userVideoRequestPersisted.enableTranscription,
        blurVideo: this.userVideoRequestPersisted.blurVideo,
      };
      EventBus.$emit("openVideoModal", videoRoomModalProps);
    },
    isTrueOrFalse(value) {
      if (value === true || value === 'true') return true;
      return false;
    }
  },
  watch: {
    currentVideoRoom: {
      handler(videoRoom) {
        if (!this.magicToken && videoRoom) {
          if (videoRoom.participants && videoRoom.participants.length > 0) {
            const isCurrentAgencyJoined = videoRoom.participants.find(item => item.ParticipantIdentity === this.participant)
            if (!isCurrentAgencyJoined) {
              this.joined = false
            }
          } else {
            this.joined = false
          }
        }
      },
      deep: true,
      immediate: true,
    },
  }
};
</script>

<style scoped>
.video-in-progress {
  background-color: #b7f1e0 !important;
  color: #28a745 !important;
}

.video-completed {
  color: #6c757d !important;
  background-color: #dde1e4 !important;
}

.video-pending {
  color: #6c757d !important;
  background-color: #dde1e4 !important;
}

.video-room-actions {
  gap: 12px;
}
</style>
